import { isNil, requireTrue } from './his-value.utils';

/**
 * Asserts whether the given value is an actual object. Does not consider `null` as object,
 * even though it is technically of type 'object' in JavaScript.
 * @param obj the value to check
 * @returns `true` if the value is an object, `false` otherwise
 */
export function isObject( obj: unknown ): obj is object {
    return !!obj && typeof obj === 'object';
}

/**
 * Asserts whether the given `obj` is a _plain_ object, created using
 * - object literal syntax: `{}`
 * - `new Object()`
 * - 'null-Object' via `Object.create(null)`
 * 
 * Any class instances or arrays will yield false, as they aren't considered plain objects.
 * @param obj the value to check
 * @returns `true` if the object is a plain object, `false` otherwise
 */
export function isPlainObject( obj: object ): boolean {
    const proto = Object.getPrototypeOf( obj );
    return isObject( obj ) && (
        proto === Object.prototype
        || proto === null
        || Object.getPrototypeOf( proto ) === null
    );
}

/**
 * Asserts whether the given object is empty, aka. `{}`. Only considers enumerable keys, meaning symbol properties are ignored.
 */
export function isEmptyObject( obj: unknown ): boolean {
    return isObject( obj ) && Object.keys( obj ).length === 0;
}

/**
 * Asserts whether the given object is **non**-empty and has at least one declared property, regardless of its value. Only considers enumerable keys, meaning symbol properties are ignored.
 */
export function isNonEmptyObject( obj: unknown ): boolean {
    return isObject( obj ) && Object.keys( obj ).length > 0;
}

/**
 * Allows for concise handling of potentially empty/nullish objects, using the `orElse` pattern
 * to provide a fallback value. Comparable to the `??` operator, but considers empty objects as nullish.
 * 
 * Removes the need for verbose nullish checks when _empty_ objects are considered equivalent to `null` or `undefined`.
 * @param obj the object to check
 * @returns a chained `orElse` function to supply the fallback value
 * @example ```ts
 * const result = nonEmptyObject({}).orElse({ default: 'value' });
 * console.log(result); // { default: 'value' }
 * ```
 */
export function nonEmptyObject<T extends object>( obj: T ) {
    return {
        orElse( value: T ) {
            return isNonEmptyObject( obj ) ? obj : value;
        }
    };
}

/**
 * Removes properties with `undefined` values from the given object, so that only explicitly defined properties remain.
 * **Mutates the original object!**
 * @param obj the object to clean
 * @param treatNullAsUndefined if `true`, properties with `null` values will also be removed
 * @returns the cleaned object, never `null` or `undefined`
 * @example ```ts
 * const obj = { a: 1, b: undefined, c: null };
 * cleanObject(obj);       // { a: 1, c: null }
 * cleanObject(obj, true); // { a: 1 }
 * ```
 */
export function cleanObject<T extends object>( obj: T, treatNullAsUndefined = false ): T {
    if ( isNil( obj ) ) {
        return {} as T;
    }
    requireTrue( isPlainObject( obj ), 'cleanObject() expects a plain object ({})' );

    for ( const key in obj ) {
        if ( obj[key] === undefined || ( treatNullAsUndefined && obj[key] === null ) ) {
            delete obj[key];
        }
    }
    return obj;
}

/**
 * Defines default values for an object. If a property is `undefined` in the object, it will be set to the corresponding value from the `defaults`.\
 * **Mutates the original object!**
 * 
 * If the object is `null` or `undefined`, a new object will be created with the default values.
 * @param obj the object to define defaults for
 * @param defaults the default values to apply
 * @returns the object with defaults applied
 */
export function defineDefaults<T extends object>( obj: T, defaults: Partial<T> ): T {
    if ( isNil( obj ) ) {
        return { ...defaults } as T;
    }
    for ( const [key, value] of entries( defaults ) ) {
        if ( obj[key] === undefined ) {
            obj[key] = value;
        }
    }
    return obj;
}

/** Strongly typed version of `Object.entries` */
export function entries<T extends object>( obj: T ): [keyof T, T[keyof T]][] {
    return Object.entries( obj ) as [keyof T, T[keyof T]][];
}

/** Strongly typed version of `Object.keys` */
export function keys<T extends object>( obj: T ): (keyof T)[] {
    return Object.keys( obj ) as (keyof T)[];
}

/** Strongly typed version of `Object.values` */
export function values<T extends object>( obj: T ): T[keyof T][] {
    return Object.values( obj ) as T[keyof T][];
}